'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { twMerge } from 'tailwind-merge';
import { Icon, IconName } from '~/components/core/Icon';
import Button from '~/components/customComponents/buttons/Button';
import Card from '~/components/customComponents/cards/Card';
import Flex from '~/components/customComponents/flex/Flex';
import Text from '~/components/customComponents/texts/Text';
import { AndercoreExternalLinks } from '~/constants/endpoints';
import { ROUTES_MAP } from '~/constants/routes';
import { useCurrentLocale, useI18n } from '~/locales/client';
import { AcceptedCookiePolicyType } from '~/types-and-enums/cookieTypes';
import { spacing } from '~/utils/tailwindUtils';

type CookieCardProps = {
  onOpen: () => void;
  acceptedCookiePolicy: AcceptedCookiePolicyType;
  onAcceptAll: () => void;
};

/**
 * Renders a modal with accept or customise user cookie preferences.
 * @param onOpen - Function to open the modal
 * @param acceptedCookiePolicy - User accepted cookie policy
 * @param onAcceptAll - Function to accept all cookies
 */
const CookieCard = ({ onOpen, acceptedCookiePolicy, onAcceptAll }: CookieCardProps) => {
  const translate = useI18n();
  const locale = useCurrentLocale();
  const pathname = usePathname();
  const isChoosePage = pathname?.includes(ROUTES_MAP.authChoose);

  // Don't show cookie banner on choose page
  if (isChoosePage || acceptedCookiePolicy) return null;

  const bottomClass = pathname.includes('/auth/')
    ? 'bottom-0'
    : pathname.includes(`${ROUTES_MAP.offersPending}/`) ||
      pathname.includes(`${ROUTES_MAP.checkout}`)
    ? 'bottom-58'
    : 'bottom-16';

  return (
    <Card
      className={twMerge(
        'fixed left-0 right-0 bg-secondary z-100 rounded-none p-0 border-none md:bottom-0',
        bottomClass,
      )}
    >
      <Flex className="justify-between gap-4 md:gap-8 flex-col md:flex-row p-4 md:p-8">
        <Flex className="items-start md:items-center flex-col md:flex-row gap-4 md:gap-8">
          <Flex className="hidden md:flex">
            <Icon
              name={IconName.Cookies}
              width={72}
              height={72}
              className="w-18 h-18"
              color="white"
              customStrokeWidth={0.6}
            />
          </Flex>
          <Flex className="flex-col gap-2">
            <Text variant="h3" color="white">
              {translate('cookieBanner.toast.title')}
            </Text>
            <Flex>
              <Text color="white">
                {translate('cookieBanner.toast.text')}{' '}
                <span className="underline underline-offset-2 cursor-pointer" onClick={onOpen}>
                  {`"${translate('cookieBanner.toast.button.customise')}"`}
                </span>
              </Text>
            </Flex>

            <Link href={AndercoreExternalLinks?.[locale]?.privacy ?? ''}>
              <Text color="white" className="underline underline-offset-2">
                {translate('cookieBanner.toast.privacy-policy')}
              </Text>
            </Link>
          </Flex>
        </Flex>
        <Flex className="items-start md:items-end self-start md:self-center">
          <Button
            onClick={onAcceptAll}
            text={translate('cookieBanner.toast.button.accept')}
            icon={<Icon name={IconName.Check} size={spacing[5]} customStrokeWidth={1.75} />}
            buttonClassName="w-fit px-6 bg-white text-secondary hover:bg-white"
            textClassName="text-primary capitalize text-nowrap"
          />
        </Flex>
      </Flex>
    </Card>
  );
};

export default CookieCard;
